import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { updateData, getDocument, getDocuments, updateUserSettings, getData } from "./utils/firestoreService";
import { useAuth } from "./utils/authContext";
import { getAuthHeaders } from "./utils/apiUtils";
import { refreshPatientSummary } from "./utils/patientDirectoryService";
import { Alert, Button, Pagination, Tabs, Tab } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { BookOpenText, FileSearch, FileText, History, Speech, UserRoundPen, Trash2, Spotlight, LibraryBig, TreeDeciduous, Share2, SquareChevronRight, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, SlidersHorizontal, RefreshCw, X } from 'lucide-react';
import PatientSelectModal from "./components/PatientSelectModal";
import SubTabs from "./tabs/subtabs";
import { mostRecentHistory, analyzeOverallSeverityRank  } from "./utils/historyProcessing";
import { documentsPerPage, tagWeight } from "./globals";
import { useSidebar } from "./contexts/SidebarContext";
import ModalPortal from "./utils/ModalPortal";
import ArchiveDocumentConfirmModal from "./components/document/ArchiveDocumentConfirmModal";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import AddEditPatient from "./utils/AddEditPatient";
import SharePatient from "./utils/SharePatient";
import PatientOverview from "./utils/patientOverview";
import { showDocumentDetails, calculateDuration, getDocDisplayState, getDocSelectedField, hasExpandableContent } from "./utils/patientUtils";
import { logAuditEventAsync, AuditActions } from "./utils/auditLogger";
import { getActiveShareForUser, canPerformAction } from "./utils/sharingService";
import { useFeatureFlags } from "./contexts/FeatureFlagContext";
import { meetsMinRole } from "./utils/roleUtils";
import { formatExportDate, getDocumentDateSortValue, sortDocumentsByDateDesc } from "./utils/documentSort";
import { useAppShellContext } from "./contexts/AppShellContext";
import { getShortSummaryDisplayContent } from "./utils/shortSummaryUtils";
import { renderRiskBadge } from "./utils/PatientsUtils";
import { formatUserDisplayName } from "./utils/userProfile";

const Patient = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientId, processingDocId, processingTitle, openRecordHeader, pendingAction: navPendingAction } = location.state || {};
  
  const { currentUser } = useAuth();
  const { flags } = useFeatureFlags();
  const { openActivityBar, closeActivityBar } = useAppShellContext();
  const userId = currentUser?.uid;
  const [patientData, setPatientData] = useState(null);
  const [personName, setPersonName] = useState("");
  const [patientLoad, setPatientLoad] = useState({ status: 'loading' });
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [patientDocuments, setPatientDocuments] = useState([]);
  const [historySummary, setHistorySummary] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortUiField, setSortUiField] = useState("date");
  const [sortUiDirection, setSortUiDirection] = useState("desc");
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [filterTypeUi, setFilterTypeUi] = useState("all");
  const [filterDateUi, setFilterDateUi] = useState("any");
  const [filterDoctorUi, setFilterDoctorUi] = useState("any");
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteDocId, setDeleteDocId] = useState(null);
  const [activeTab, setActiveTab] = useState(null); // Will be set based on userRole
  const [activeSubTab, setActiveSubTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [documentCategoryCount, setDocumentCategoryCount] = useState(0);
  const [settings, setSettings] = useState({});
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(true);
  const [processingDocumentId, setProcessingDocumentId] = useState(null);
  const [processingStatus, setProcessingStatus] = useState({});
  const [shortSummaryProcessing, setShortSummaryProcessing] = useState({});
  const [isEditPatientOpen, setEditPatientOpen] = useState(false);
  const [isSharePatientOpen, setSharePatientOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [overallSeverityData, setOverallSeverityData] = useState(null);
  const [docDisplayStates, setDocDisplayStates] = useState({});
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [isMissingInfo, setIsMissingInfo] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [primaryFields, setPrimaryFields] = useState([]);
  // Userback 7583177: cache of { firstName, lastName, degree } keyed by uid for
  // showing per-document author + credentials on shared-in patients.
  const [authorProfiles, setAuthorProfiles] = useState({});
  const isCurrentPatientLoad = patientLoad.patientId === patientId && patientLoad.userId === userId;
  const isPatientReady = isCurrentPatientLoad && patientLoad.status === 'ready';

  // Check if user has elevated role to see advanced tabs (Focus View, Life Story)
  const showAdvancedTabs = meetsMinRole(userRole, 'manager');

  // Compute view-only status - true if this is a shared patient who cannot edit
  // patient info (anything less than `full`). Used to gate the Edit Patient button.
  const isViewOnly = shareInfo && !canPerformAction(shareInfo, 'edit');

  // Human-readable label for the share-access badge next to the patient name.
  const sharedAccessBadgeLabel = (() => {
    if (!shareInfo?.accessLevel) return null;
    switch (shareInfo.accessLevel) {
      case 'view': return 'View Only';
      case 'view_add': return 'View + Add';
      case 'view_add_private': return 'View + Add';
      case 'full': return 'Full Access';
      default: return 'Shared';
    }
  })();

  const { sidebarCollapsed, isProcessing } = useSidebar();

  const indexOfLastDocument = currentPage * documentsPerPage;
  const indexOfFirstDocument = indexOfLastDocument - documentsPerPage;

  useEffect(() => {
    let cancelled = false;
    setPatientLoad({ patientId, userId, status: 'loading' });
    setPatientData(null);
    setPersonName("");
    setPatientDocuments([]);
    setHistorySummary(null);
    setShareInfo(null);
    setAuthorProfiles({});
    setOverallSeverityData(null);
    setDocumentCategoryCount(0);
    setCurrentPage(1);
    setSearchQuery("");
    setSettings({});
    setDocDisplayStates({});
    setPrimaryFields([]);
    setUserRole(null);
    setActiveTab(null);
    setEditPatientOpen(false);
    setSharePatientOpen(false);
    setDeleteConfirmOpen(false);
    setIsLoadingDocuments(true);
    if (!patientId || !userId) return;

    const fetchPatientData = async () => {
      try {
        const loadedPatient = await getDocument('patients', patientId);
        if (cancelled) return;
        if (!loadedPatient) {
          throw Object.assign(new Error('Patient unavailable'), { code: 'not-found' });
        }

        // These reads are independent. Read the profile once for role/settings,
        // and publish only after access metadata is known (a failure is not ownership).
        const [loadedDocuments, userDoc, share, allDataFields] = await Promise.all([
          getDocuments(`patients/${patientId}/documents`),
          getData(`/users/${userId}`),
          getActiveShareForUser(userId, patientId),
          getDocuments('documentDataFields').catch(error => {
            console.error('Error fetching primary fields:', error);
            return [];
          }),
        ]);
        if (cancelled) return;
        if (!userDoc?.userRole) throw new Error('User profile unavailable');

        const documents = sortDocumentsByDateDesc(loadedDocuments);
        const visibleDocuments = documents.filter(doc => !doc.isArchived);
        const userSettings = userDoc.settings || {};
        const mostRecent = mostRecentHistory(documents);
        setPatientData(loadedPatient);
        setPersonName(`${loadedPatient.firstName} ${loadedPatient.lastName}`);
        setIsMissingInfo(!loadedPatient.dateOfBirth || !loadedPatient.pronoun);
        setPatientDocuments(visibleDocuments);
        setProcessingDocumentId(id => visibleDocuments.some(doc => doc.id === id && !doc.isOptimistic) ? null : id);
        setDocumentCategoryCount(documents.filter(doc => doc.category === 1).length);
        setHistorySummary(mostRecent && typeof mostRecent === 'object' ? mostRecent : null);
        setSettings(userSettings);
        setDocDisplayStates(userSettings.docDisplayStates || {});
        setPrimaryFields(allDataFields.filter(field => field.primaryField)
          .map(field => ({ fieldName: field.fieldName, displayName: field.displayName })));
        setUserRole(userDoc.userRole);
        setShareInfo(share);
        setPatientLoad({ patientId, userId, status: 'ready' });
        logAuditEventAsync(AuditActions.PATIENT_VIEW, {
          resourceType: 'patient', resourceId: patientId, patientId,
        });
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching patient data:", error);
        const unavailable = ['not-found', 'permission-denied'].includes(error.code);
        setPatientLoad({ patientId, userId, status: 'error', message: unavailable
          ? 'This patient is unavailable. It may have been removed, or you may no longer have access.'
          : "We couldn't load this patient. Please try again." });
      } finally {
        if (!cancelled) setIsLoadingDocuments(false);
      }
    };

    fetchPatientData();
    return () => { cancelled = true; };
  }, [patientId, userId, retryAttempt]);

  // Owned patients can also contain notes from private contributors.
  // Resolve authors through the patient-scoped endpoint for both directions.
  useEffect(() => {
    if (!patientId) return;
    const uids = [...new Set(
      patientDocuments.map((d) => d.createdBy).filter(Boolean)
    )];
    const missing = uids.filter((u) => !(u in authorProfiles));
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await fetch('/api/v3/users/public-profiles', {
          method: 'POST',
          headers,
          body: JSON.stringify({ patientId, uids: missing }),
        });
        if (!res.ok || cancelled) return;
        const { profiles } = await res.json();
        if (!cancelled && profiles) {
          // Remember missing profiles too; otherwise an empty response triggers
          // a fetch/render loop for deleted or legacy authors.
          setAuthorProfiles((prev) => ({ ...prev, ...Object.fromEntries(missing.map(uid => [uid, profiles[uid] || null])) }));
        }
      } catch (err) {
        console.error('[Patient] Failed to fetch author profiles:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [patientDocuments, shareInfo, authorProfiles, patientId]);

  // Set initial activeTab based on userRole
  // For regular users (standard), default to Library (tab 0)
  // For elevated users (manager, superAdmin), default to Library (tab 1)
  useEffect(() => {
    if (activeTab === null && userRole !== null) {
      const isElevatedUser = meetsMinRole(userRole, 'manager');
      setActiveTab(isElevatedUser ? 1 : 0);
    }
  }, [userRole, activeTab]);

  // Handle optimistic UI for processing document
  useEffect(() => {
    if (processingDocId && processingTitle) {
      setProcessingDocumentId(processingDocId);
      // Add optimistic document to the list
      const optimisticDoc = {
        id: processingDocId,
        title: processingTitle || "Processing...",
        documentDate: new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }),
        status: "processing",
        typeId: 1, // Assuming audio recording
        isOptimistic: true,
      };
      setPatientDocuments((prev) => [optimisticDoc, ...prev]);
    }
  }, [processingDocId, processingTitle]);

  // Listen for document completion events to refresh the list
  useEffect(() => {
    if (!isPatientReady) return;
    let cancelled = false;
    const handleDocumentRefresh = async (event) => {
      const { patientId: eventPatientId, newDocId, action } = event.detail || {};
      
      if (eventPatientId === patientId) {
        try {
          // Fetch fresh documents from Firestore
          let documents = await getDocuments(`patients/${patientId}/documents`);
          if (cancelled) return;

          documents = sortDocumentsByDateDesc(
            documents.filter(doc => !doc.isArchived)
          );

          setPatientDocuments(documents);
          
          // Update processing status based on document state
          const updatedProcessingStatus = {};
          const updatedShortSummaryStatus = {};
          documents.forEach(doc => {
            if (doc.status === "in progress" || doc.status === "processing") {
              updatedProcessingStatus[doc.id] = true;
              // Check if short summary is still processing
              if (doc.continueReceived && !doc.short_summary?.summary) {
                updatedShortSummaryStatus[doc.id] = true;
              }
            } else if (doc.status === "completed") {
              updatedProcessingStatus[doc.id] = false;
              updatedShortSummaryStatus[doc.id] = false;
            }
          });
          setProcessingStatus(updatedProcessingStatus);
          setShortSummaryProcessing(updatedShortSummaryStatus);
        } catch (error) {
          console.error('Error refreshing documents:', error);
        }
      }
    };

    const handleProcessingUpdate = (event) => {
      const { docId, status, continueReceived, shortSummaryComplete } = event.detail || {};
      if (docId) {
        setProcessingStatus(prev => ({
          ...prev,
          [docId]: status === "in progress" || status === "processing"
        }));
        
        if (continueReceived) {
          // If continue received but short summary not done, show short summary processing
          setShortSummaryProcessing(prev => ({
            ...prev,
            [docId]: !shortSummaryComplete
          }));
        }
        
        if (shortSummaryComplete) {
          setShortSummaryProcessing(prev => ({
            ...prev,
            [docId]: false
          }));
        }
      }
    };

    window.addEventListener('refreshPatientDocuments', handleDocumentRefresh);
    window.addEventListener('documentProcessingUpdate', handleProcessingUpdate);
    
    return () => {
      cancelled = true;
      window.removeEventListener('refreshPatientDocuments', handleDocumentRefresh);
      window.removeEventListener('documentProcessingUpdate', handleProcessingUpdate);
    };
  }, [patientId, isPatientReady]);

  // Handle opening record header when navigated from Dashboard mic icon
  useEffect(() => {
    if (openRecordHeader && patientId && isPatientReady) {
      // Small delay to ensure page is loaded before triggering header
      const timer = setTimeout(() => {
        // Tell AppShell to open the recording flow for this patient.
        window.dispatchEvent(new CustomEvent('openRecordHeader', { 
          detail: { patientId } 
        }));
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [openRecordHeader, patientId, isPatientReady]);

  // Resume intended action (record/upload/export) after patient selection
  useEffect(() => {
    if (navPendingAction && patientId && isPatientReady) {
      const timer = setTimeout(() => {
        openActivityBar(navPendingAction);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [navPendingAction, patientId, isPatientReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate overall severity data when documents change
  useEffect(() => {
    if (patientDocuments && patientDocuments.length > 0) {
      const severityAnalysis = analyzeOverallSeverityRank(patientDocuments);
      setOverallSeverityData(severityAnalysis);
    }
  }, [patientDocuments]);

const handleBack = () => {
  const from = location.state?.from;
  navigate(from || '/patients');
};

  const handlePatientUpdate = (updatedPatientData) => {
    const formattedPersonName = `${updatedPatientData.firstName} ${updatedPatientData.lastName}`;
    setPersonName(formattedPersonName);
    setIsMissingInfo(!updatedPatientData.dateOfBirth || !updatedPatientData.pronoun);
    setPatientData(previous => previous ? { ...previous, ...updatedPatientData } : previous);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSubTabChange = useCallback((event, newValue) => {
    setActiveSubTab(newValue);
  }, []);

  const subTabsMemo = useMemo(() => {
    if (historySummary && patientDocuments) {
      return <SubTabs patientId={patientId} activeSubTab={activeSubTab} handleSubTabChange={handleSubTabChange} historySummary={historySummary} patientDocuments={patientDocuments} settings={settings} />;
    }
    return <div className="history-summary-section">History data will appear after you complete a Progress Note for this patient.</div>;
  }, [activeSubTab, handleSubTabChange, historySummary, patientDocuments, settings, patientId]);

  const handleDeleteDocument = async (docId) => {
    await updateData(`patients/${patientId}/documents`, docId, {
      isArchived: true,
      modifiedAt: new Date(),
    });
    await refreshPatientSummary(userId, patientId).catch((error) => {
      console.warn("[Patient] Failed to refresh list summary after archive:", error.message);
    });
    logAuditEventAsync(AuditActions.DOCUMENT_ARCHIVE, {
      resourceType: 'document',
      resourceId: docId,
      patientId: patientId,
    });
    // Remove from search index (fire-and-forget)
    getAuthHeaders().then(headers =>
      fetch(`/api/search/index/doc_${patientId}_${docId}`, { method: 'DELETE', headers })
    ).catch(() => {});
    setPatientDocuments((prevDocs) => prevDocs.filter((doc) => doc.id !== docId));
  };

  const handleDocumentClick = (docId) => {
    // Pass processingDocId if this is the document being processed
    const navigationState = { patientId, docId };
    if (processingDocId === docId) {
      navigationState.processingDocId = processingDocId;
    }
    // Thread through the origin so Document back → Patient back returns to the right place
    if (location.state?.from) {
      navigationState.from = location.state.from;
    }
    navigate("/document", { state: navigationState });
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleToggleFilterExpanded = () => {
    setIsFilterExpanded((prev) => !prev);
  };

  const activeFilterCount = [
    filterTypeUi !== 'all',
    filterDateUi !== 'any',
    shareInfo && filterDoctorUi !== 'any',
  ].filter(Boolean).length;

  const handleClearFilters = () => {
    setFilterTypeUi('all');
    setFilterDateUi('any');
    setFilterDoctorUi('any');
  };

  const handleCloseFilters = () => {
    setIsFilterExpanded(false);
  };

  const handleSortUiClick = (field) => {
    if (sortUiField !== field) {
      setSortUiField(field);
      setSortUiDirection(field === 'date' ? 'desc' : 'asc');
      return;
    }

    if (sortUiDirection === 'desc') {
      setSortUiDirection('asc');
      return;
    }

    if (sortUiDirection === 'asc') {
      setSortUiField(null);
      setSortUiDirection(null);
    }
  };

  const filterSummary = (() => {
    const parts = [];
    if (filterTypeUi !== 'all') parts.push(filterTypeUi === 'summary' ? 'Summary' : filterTypeUi === 'upload' ? 'Upload' : 'Recording');
    if (filterDateUi !== 'any') parts.push(filterDateUi === '7d' ? 'Last 7 days' : filterDateUi === '30d' ? 'Last 30 days' : 'Last 90 days');
    if (shareInfo && filterDoctorUi !== 'any') parts.push(filterDoctorUi === 'mine' ? 'My documents' : 'Shared doctor');
    return parts.join(' | ');
  })();

  // Two-state display toggle: normal ↔ expanded
  // docDisplayStates values can be a string ('normal'|'expanded') or
  // an object { state, fields } for expanded views with chip selections
  const cycleDocDisplayState = useCallback((docId) => {
    setDocDisplayStates(prev => {
      const current = getDocDisplayState(prev[docId]);
      const next = current === 'normal' ? 'expanded' : 'normal';
      // Preserve last selected field if transitioning back to expanded
      const prevField = getDocSelectedField(prev[docId]);
      const updated = { ...prev, [docId]: next === 'expanded' && prevField ? { state: next, field: prevField } : next };
      // Persist to Firestore (fire-and-forget)
      if (userId) {
        updateUserSettings(userId, { docDisplayStates: updated }).catch(err => 
          console.error('Error saving display state:', err)
        );
      }
      return updated;
    });
  }, [userId]);

  // Select a single field chip in expanded view for a specific document
  const selectExpandedField = useCallback((docId, fieldName) => {
    setDocDisplayStates(prev => {
      const updated = { ...prev, [docId]: { state: 'expanded', field: fieldName } };
      if (userId) {
        updateUserSettings(userId, { docDisplayStates: updated }).catch(err =>
          console.error('Error saving field selection:', err)
        );
      }
      return updated;
    });
  }, [userId]);

  const searchInObject = (obj, searchTerm) => {
    if (!obj) return false;
    return Object.values(obj).some((value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(searchTerm.toLowerCase());
      }
      if (typeof value === "object") {
        return searchInObject(value, searchTerm);
      }
      return false;
    });
  };

  const matchesTypeFilter = (doc) => {
    if (filterTypeUi === 'all') return true;

    const sourceType = (doc?.sourceType || '').toLowerCase();

    if (filterTypeUi === 'recording') {
      return sourceType === 'recording';
    }

    if (filterTypeUi === 'upload') {
      return sourceType === 'upload';
    }

    if (filterTypeUi === 'summary') {
      return sourceType === 'summary';
    }

    return true;
  };

  const matchesDateFilter = (doc) => {
    if (filterDateUi === 'any') return true;

    const timestamp = getDocumentDateSortValue(doc);
    if (!timestamp || timestamp <= 0) return false;

    const now = Date.now();
    const windowMs =
      filterDateUi === '7d'
        ? 7 * 24 * 60 * 60 * 1000
        : filterDateUi === '30d'
          ? 30 * 24 * 60 * 60 * 1000
          : 90 * 24 * 60 * 60 * 1000;

    return now - timestamp <= windowMs;
  };

  const matchesDoctorFilter = (doc) => {
    // Doctor filtering is only relevant in shared-patient context.
    if (!shareInfo || filterDoctorUi === 'any') return true;

    const createdBy = doc?.createdBy;

    if (filterDoctorUi === 'mine') {
      return createdBy === userId;
    }

    if (filterDoctorUi === 'shared') {
      return createdBy ? createdBy !== userId : true;
    }

    return true;
  };

  const filteredDocuments = patientDocuments.filter((doc) => {
    // Background History Summary records stay out of Library. Patient overview
    // readers select saved summaries separately from Library source documents.
    if (doc.type === "historySummary") return false;

    // Hide documents stuck in processing for over 20 minutes
    const isProcessing = processingStatus[doc.id] || doc.status === "processing" || doc.status === "in progress";
    if (isProcessing && !doc?.short_summary) {
      const createdAt = doc.createdAt?.toDate ? doc.createdAt.toDate() : doc.createdAt?.seconds ? new Date(doc.createdAt.seconds * 1000) : null;
      if (createdAt && (Date.now() - createdAt.getTime() > 20 * 60 * 1000)) return false;
    }

    if (!matchesTypeFilter(doc)) {
      return false;
    }

    if (!matchesDateFilter(doc)) {
      return false;
    }

    if (!matchesDoctorFilter(doc)) {
      return false;
    }

    const searchTerm = searchQuery.toLowerCase();
    return (
      doc.title?.toLowerCase().includes(searchTerm) ||
      doc.document_summary?.summary?.toLowerCase().includes(searchTerm) ||
      doc.documentDate?.toLowerCase().includes(searchTerm) ||
      doc.interval_history?.summary?.toLowerCase().includes(searchTerm) ||
      doc.history_goals?.summary?.toLowerCase().includes(searchTerm) ||
      doc.history_summary?.summary?.toLowerCase().includes(searchTerm) ||
      doc.cleaned_text?.summary?.toLowerCase().includes(searchTerm) ||
      searchInObject(doc.sections, searchTerm)
    );
  });

  const sortedDocuments = useMemo(() => {
    if (!sortUiField || !sortUiDirection) {
      return filteredDocuments;
    }

    const indexed = filteredDocuments.map((doc, idx) => ({ doc, idx }));

    indexed.sort((a, b) => {
      let cmp = 0;

      if (sortUiField === 'name') {
        const aName = (a.doc?.title || '').toLowerCase();
        const bName = (b.doc?.title || '').toLowerCase();
        cmp = aName.localeCompare(bName, undefined, { sensitivity: 'base' });
        if (cmp === 0) {
          // Stable tie-break for readability when titles match.
          cmp = getDocumentDateSortValue(b.doc) - getDocumentDateSortValue(a.doc);
        }
      } else if (sortUiField === 'date') {
        cmp = getDocumentDateSortValue(a.doc) - getDocumentDateSortValue(b.doc);
        if (cmp === 0) {
          const aName = (a.doc?.title || '').toLowerCase();
          const bName = (b.doc?.title || '').toLowerCase();
          cmp = aName.localeCompare(bName, undefined, { sensitivity: 'base' });
        }
      }

      if (cmp === 0) {
        cmp = a.idx - b.idx;
      }

      return sortUiDirection === 'asc' ? cmp : -cmp;
    });

    return indexed.map((entry) => entry.doc);
  }, [filteredDocuments, sortUiField, sortUiDirection]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortUiField, sortUiDirection, filterTypeUi, filterDateUi, filterDoctorUi]);

  useEffect(() => {
    if (!shareInfo && filterDoctorUi !== 'any') {
      setFilterDoctorUi('any');
    }
  }, [shareInfo, filterDoctorUi]);

  const currentDocuments = sortedDocuments.slice(indexOfFirstDocument, indexOfLastDocument);
  const totalPages = Math.ceil(sortedDocuments.length / documentsPerPage);

  // No patient context — show modal to search/add patient
  if (!patientId) {
    return (
      <PatientSelectModal
        isOpen={true}
        onPatientSelect={(patient) => {
          if (patient?.id) {
            navigate("/patient", {
              replace: true,
              state: {
                patientId: patient.id,
                // Forward the intended action so it triggers after patient loads
                ...(navPendingAction ? { pendingAction: navPendingAction } : {}),
              },
            });
          }
        }}
        onCancel={() => {
          navigate("/patients", { replace: true });
        }}
      />
    );
  }

  if (!isPatientReady) {
    const error = isCurrentPatientLoad && patientLoad.status === 'error';
    return (
      <div className={`page-container${sidebarCollapsed ? " wide" : ""}`}>
        <Box sx={{ p: 3 }}>
          {error ? <Alert severity="error">{patientLoad.message}</Alert>
            : <div role="status">Loading patient…</div>}
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            {error && <Button variant="contained" onClick={() => setRetryAttempt(attempt => attempt + 1)}>Try again</Button>}
            <Button variant="outlined" onClick={() => navigate('/patients')}>Back to patients</Button>
          </Box>
        </Box>
      </div>
    );
  }

  return (
    <div className={`page-container${sidebarCollapsed ? " wide" : ""}`}>
      <div className={`header-container elevated-header${sidebarCollapsed ? " wide" : ""}`}>
        <div className="header-left">
          <Tooltip title="Go back" arrow>
            <span style={{ display: 'inline-flex' }}>
              <button 
                className="back-button" 
                onClick={handleBack}
                aria-label="Go back"
              >
                <SquareChevronRight size={18} style={{ transform: 'scaleX(-1)' }} />
              </button>
            </span>
          </Tooltip>
          {personName && (
            <div className="page-title userback-ignore">
              Patient Records for {personName}
              {sharedAccessBadgeLabel && (
                <span style={{
                  marginLeft: '12px',
                  padding: '2px 8px',
                  backgroundColor: 'var(--color-light-gray)',
                  color: 'var(--color-dark-gray)',
                  borderRadius: '4px',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 500,
                  verticalAlign: 'middle'
                }}>
                  {sharedAccessBadgeLabel}
                </span>
              )}
              <Tooltip title={isViewOnly ? "Only the patient owner can edit patient info" : (isMissingInfo ? "Edit Patient - Missing demographic info" : "Edit Patient")} arrow>
                <span className={`header-edit-icon-wrapper${isViewOnly ? ' disabled' : ''}`}>
                  <UserRoundPen 
                    className="header-edit-icon" 
                    onClick={isViewOnly ? undefined : () => setEditPatientOpen(true)} 
                    size={14}
                  />
                  {isMissingInfo && !isViewOnly && (
                    <span className="header-edit-icon-badge" />
                  )}
                </span>
              </Tooltip>
              {meetsMinRole(userRole, 'standard') && !shareInfo && (
                <Tooltip title="Share Patient" arrow>
                  <span className="header-edit-icon-wrapper">
                    <Share2 
                      className="header-edit-icon" 
                      onClick={() => setSharePatientOpen(true)} 
                      size={14}
                    />
                  </span>
                </Tooltip>
              )}
            </div>
          )}
        </div>

        <div className="header-right">
          {/* Update History — P1 feature, hidden behind `historyUpdate` flag until implemented.
              Will trigger a background "historyUpdate" document that re-runs history
              summary fields for this patient. See docs/features/document-system/history-optimization.md. */}
          {flags?.historyUpdate && personName && documentCategoryCount > 30 && (
            <div className="header-button">
              {/* <UpdateIcon className="header-icon" /> Update History */}
              <Tooltip title="Update History" arrow>
                <History className="header-icon" />
              </Tooltip>
              Update History
            </div>
          )}
        </div>
      </div>
      <div className="patient-content-container">
        <div className="patient-tabs-container">
{activeTab !== null && (
<Tabs value={activeTab} onChange={handleTabChange} className="patient-tabs" scrollButtons="auto">
  {showAdvancedTabs && (
    <Tab label={
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Spotlight size={14} />
        Focus View
      </div>
    } className="patient-tab" wrapped />
  )}
  <Tab label={
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <LibraryBig size={14} />
      Library
    </div>
  } className="patient-tab" wrapped />
  {showAdvancedTabs && (
    <Tab label={
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <TreeDeciduous size={14} />
        Life Story
      </div>
    } className="patient-tab" wrapped />
  )}
</Tabs>
)}
        </div>

  {/* For elevated users: tab 0=Focus, 1=Library, 2=Life Story */}
  {/* For standard users: tab 0=Library (only tab shown) */}
  {showAdvancedTabs && activeTab === 0 && (
          <PatientOverview 
            patientData={patientData}
            historySummary={historySummary}
            overallSeverityData={overallSeverityData}
            settings={settings}
          />
        )}

        {/* Library tab: tab 1 for elevated users, tab 0 for standard users */}
        {((showAdvancedTabs && activeTab === 1) || (!showAdvancedTabs && activeTab === 0)) && (
          <div className="documents-list-container">
            <div className="documents-grid">
              <div className="documents-table-section">
                <div className="patient-header-container">
                  <div className={`documents-toolbar-row userback-ignore ${isFilterExpanded ? 'filter-expanded' : ''}`}>
                    <div className="documents-toolbar-search">
                      <div className="header-search-wrapper">
                        <FileSearch className="header-search-icon" />
                        <input type="text" placeholder={isFilterExpanded ? 'Search' : 'Search documents'} value={searchQuery} onChange={handleSearchChange} className="header-search-input userback-ignore" />
                      </div>
                    </div>

                    <div className="documents-toolbar-controls">
                      <div className="documents-inline-control documents-inline-control-filter" role="group" aria-label="Filter options">
                        <span className="documents-inline-label">
                          <SlidersHorizontal size={14} />
                          Filter
                        </span>
                        {!isFilterExpanded && (
                          <Tooltip title="Show filters" arrow>
                            <button
                              type="button"
                              className={`documents-inline-box documents-filter-inline-box ${activeFilterCount === 0 ? 'compact' : ''}`}
                              onClick={handleToggleFilterExpanded}
                              aria-label="Show filters"
                            >
                              {activeFilterCount === 0 ? (
                                <SlidersHorizontal size={12} />
                              ) : (
                                <span className="documents-filter-inline-text">
                                  {filterSummary}
                                </span>
                              )}
                            </button>
                          </Tooltip>
                        )}

                        {isFilterExpanded && (
                          <div className="documents-filter-inline-expanded" role="region" aria-label="Expanded filter controls">
                            <select
                              id="documents-filter-type"
                              value={filterTypeUi}
                              onChange={(e) => setFilterTypeUi(e.target.value)}
                              title="Document type"
                            >
                              <option value="all">Type: All</option>
                              <option value="recording">Type: Recording</option>
                              <option value="upload">Type: Upload</option>
                              <option value="summary">Type: Summary</option>
                            </select>
                            <select
                              id="documents-filter-date"
                              value={filterDateUi}
                              onChange={(e) => setFilterDateUi(e.target.value)}
                              title="Date range"
                            >
                              <option value="any">Date: Any</option>
                              <option value="7d">Date: 7d</option>
                              <option value="30d">Date: 30d</option>
                              <option value="90d">Date: 90d</option>
                            </select>
                            {shareInfo && (
                              <select
                                id="documents-filter-doctor"
                                value={filterDoctorUi}
                                onChange={(e) => setFilterDoctorUi(e.target.value)}
                                title="Doctor"
                              >
                                <option value="any">Doctor: Any</option>
                                <option value="mine">Doctor: Mine</option>
                                <option value="shared">Doctor: Shared</option>
                              </select>
                            )}
                            {activeFilterCount > 0 && (
                              <Tooltip title="Reset filters" arrow>
                                <button
                                  type="button"
                                  className="documents-filter-action-button"
                                  onClick={handleClearFilters}
                                  aria-label="Reset filters"
                                >
                                  <RefreshCw size={12} />
                                </button>
                              </Tooltip>
                            )}
                            <Tooltip title="Close filters" arrow>
                              <button
                                type="button"
                                className="documents-filter-action-button"
                                onClick={handleCloseFilters}
                                aria-label="Close filters"
                              >
                                <X size={12} />
                              </button>
                            </Tooltip>
                          </div>
                        )}
                      </div>

                      <div className="documents-inline-control documents-inline-control-sort" role="group" aria-label="Sort options">
                        <span className="documents-inline-label">
                          <ArrowUpDown size={14} />
                          Sort
                        </span>
                        <div className="documents-sort-inline-options">
                          <button
                            type="button"
                            className={`documents-sort-inline-button ${sortUiField === 'name' ? 'active' : ''}`}
                            onClick={() => handleSortUiClick('name')}
                          >
                            <span>Name</span>
                            {sortUiField === 'name' ? (
                              sortUiDirection === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                            ) : (
                              <ArrowUpDown size={12} />
                            )}
                          </button>
                          <button
                            type="button"
                            className={`documents-sort-inline-button ${sortUiField === 'date' ? 'active' : ''}`}
                            onClick={() => handleSortUiClick('date')}
                          >
                            <span>Date</span>
                            {sortUiField === 'date' ? (
                              sortUiDirection === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                            ) : (
                              <ArrowUpDown size={12} />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <table className="standard-table documents-list-table">
                  <thead>
                    <tr>
                      <th className="column-5"></th>
                      <th className="column-90"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingDocuments ? (
                      <tr>
                        <td colSpan="2">
                          <Box className="table-loading-bar">
                            <LinearProgress />
                          </Box>
                        </td>
                      </tr>
                    ) : currentDocuments.length === 0 ? (
                      <tr>
                        <td colSpan="2" style={{ textAlign: "center" }}>
                          No documents found...
                        </td>
                      </tr>
                    ) : (
                      currentDocuments.map((doc, index) => {
                        const displayState = getDocDisplayState(docDisplayStates[doc.id]);
                        const docHasContent = hasExpandableContent(doc, primaryFields);
                        return (
                        <tr 
                          key={index}
                          className={`doc-state-${displayState} userback-ignore`}
                          onMouseEnter={() => setHoveredRowId(doc.id)}
                          onMouseLeave={() => setHoveredRowId(null)}
                        >
                          <td>
                            <div className="doc-icon-cell">
                              {hoveredRowId === doc.id && docHasContent ? (
                                <Tooltip title={displayState === 'normal' ? 'Expanded view' : 'Normal view'} arrow>
                                  <button
                                    className="table-icon-button"
                                    onClick={(e) => { e.stopPropagation(); cycleDocDisplayState(doc.id); }}
                                  >
                                    <ChevronRight 
                                      className={`table-icon doc-state-icon doc-state-icon-${displayState}`} 
                                      size={16} 
                                    />
                                  </button>
                                </Tooltip>
                              ) : (
                                <Tooltip title={doc.sourceType === 'Recording' ? 'Recording' : doc.sourceType === 'Upload' ? 'Import' : 'Document'} arrow>
                                  <button
                                    className="table-icon-button"
                                    onClick={() => handleDocumentClick(doc.id)}
                                  >
                                    {doc.sourceType === 'Recording' ? (
                                      <Speech className="table-icon" size={16} />
                                    ) : doc.sourceType === 'Upload' ? (
                                      <FileText className="table-icon" size={16} />
                                    ) : (
                                      <BookOpenText className="table-icon" size={16} />
                                    )}
                                  </button>
                                </Tooltip>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="document-row-content userback-ignore">
                              <h4 className="document-list-title">
                                <div className="document-title-cell">
                                  <strong
                                    className={`document-title-link ${doc.isOptimistic ? 'document-title-disabled' : ''}`}
                                    onClick={() => !doc.isOptimistic && handleDocumentClick(doc.id)}
                                    >
                                    {doc.title}
                                    {doc.isOptimistic && <span className="processing-span">(Processing...)</span>}
                                  </strong>
                                  <div className="summary-date-time">
                                    <span>{doc.documentDate}</span>
                                    {(() => {
                                      const timeRange = doc.startTime && doc.endTime ? `${doc.startTime} - ${doc.endTime}` : '';
                                      const duration = calculateDuration(timeRange);
                                      return duration ? <span> - {duration}</span> : null;
                                    })()}
                                    {doc.exportedAt && (
                                      <span>
                                        &nbsp;| Exported: {formatExportDate(doc.exportedAt)}
                                      </span>
                                    )}
                                    {/* Owner-only delete: doc author OR patient owner on legacy doc with no createdBy stamp.
                                        Per Patient Sharing authorship-lock spec: shared users cannot delete docs they didn't create. */}
                                    {hoveredRowId === doc.id && (doc.createdBy === userId || (!shareInfo && !doc.createdBy)) && (
                                      <Tooltip title="Delete Document" arrow>
                                        <Trash2 className="table-icon-lighter trash-icon" onClick={(e) => {e.stopPropagation(); setDeleteConfirmOpen(true); setDeleteDocId(doc.id); }} size={12} />
                                      </Tooltip>
                                    )} 
                                  </div>
                                  {/* Include contributor identity in the owner's library as well. */}
                                  {doc.createdBy && authorProfiles[doc.createdBy] && (() => {
                                    const label = formatUserDisplayName(authorProfiles[doc.createdBy]);
                                    return (
                                      <div className="summary-date-time document-author-line">
                                        By {label}
                                      </div>
                                    );
                                  })()}
                                  <div className="badge-wrapper">
                                    {flags?.showTags &&
                                      Array.isArray(doc.tags) &&
                                      doc.tags.length > 0 &&
                                      doc.tags
                                        .filter((tag) => tag.weight > tagWeight)
                                        .map((tag, index) => (
                                                   <Tooltip key={index} title="Recent session topics" arrow>
                                          <span key={index} className="tag-box">
                                            {tag.tag}
                                          </span>
                                          </Tooltip>
                                        ))}
                                    {renderRiskBadge({ document: doc }, flags)}
                                  </div>
                                </div>
                              </h4>
                              {displayState === 'normal' && (() => {
                                  const isProcessing = processingStatus[doc.id] || doc.status === "processing" || doc.status === "in progress";
                                  const isShortSummaryProcessing = shortSummaryProcessing[doc.id];
                                  
                                  // Hide processing indicator if document is older than 10 minutes (stuck processing)
                                  const createdAt = doc.createdAt?.toDate ? doc.createdAt.toDate() : doc.createdAt?.seconds ? new Date(doc.createdAt.seconds * 1000) : null;
                                  const isStuckProcessing = createdAt && (Date.now() - createdAt.getTime() > 10 * 60 * 1000);
                         
                                  if (isProcessing && !doc?.short_summary && !isStuckProcessing) {
                                    return (
                                      <div className="summary-small">
                                        <div className="processing-indicator">
                                          <div className="spinner"></div>
                                          <div>{isShortSummaryProcessing ? "Ready to review while processing additional history data..." : "Processing"}</div>
                                        </div>
                                      </div>
                                    );
                                  }
                                  // Handle legacy string values and repair records where a
                                  // grouped model response appended the cleaned transcript.
                                  const summaryText = getShortSummaryDisplayContent(doc) || "";
                                  if (summaryText) {
                                    const protectedText = summaryText
                                      .replace(/\b(Mr|Mrs|Ms|Dr|Prof|Sr|Jr|St|Ave|Blvd|Rd|Etc|Inc|Ltd|Co|Corp)\./gi, '$1‸');
                                    const match = protectedText.match(/^.+?[.!?](?=\s|$)/);
                                    let firstSentence = match ? match[0] : protectedText.split(/[.!?]/)[0];
                                    firstSentence = firstSentence.replace(/‸/g, '.');
                                    const truncated = firstSentence.length > 360 ? firstSentence.substring(0, 360) + "..." : firstSentence;
                                    return <div className="summary-small">{truncated}</div>;
                                  }
                                  return null;
                              })()}
                              {displayState === 'expanded' && (() => {
                                const details = showDocumentDetails(doc, primaryFields, getDocSelectedField(docDisplayStates[doc.id]), (fieldName) => selectExpandedField(doc.id, fieldName));
                                return details ? (
                                  <div className="document-expanded-details">
                                    {details}
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
                <div className="pagination-controls">{totalPages > 1 && <Pagination count={totalPages} page={currentPage} onChange={(event, value) => setCurrentPage(value)} color="primary" variant="outlined" shape="rounded" size="small" />}</div>
              </div>
            </div>
          </div>
        )}

        <ArchiveDocumentConfirmModal
          key={`${patientId}/${deleteDocId}`}
          isOpen={isDeleteConfirmOpen}
          onCancel={() => setDeleteConfirmOpen(false)}
          onConfirm={() => handleDeleteDocument(deleteDocId)}
        />

        {/* Life Story tab: only for elevated users at tab 2 */}
        {showAdvancedTabs && activeTab === 2 && subTabsMemo}
      </div>
      <AddEditPatient
        isEditPatientOpen={isEditPatientOpen}
        setEditPatientOpen={setEditPatientOpen}
        patientId={patientId}
        onPatientUpdate={handlePatientUpdate}
        onPatientArchived={() => {
          closeActivityBar();
          navigate("/patients");
        }}
      />
      {meetsMinRole(userRole, 'standard') && (
        <SharePatient
          isOpen={isSharePatientOpen}
          setIsOpen={setSharePatientOpen}
          patientId={patientId}
          patientName={personName}
        />
      )}
    </div>
  );
};

export default React.memo(Patient);
